import { useEffect, useMemo, useState } from 'react'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { useParams } from 'react-router-dom'
import {
  AlertCircle,
  Bell,
  Info,
  Loader2,
  Settings2,
  TrendingDown,
} from 'lucide-react'

import { AutomationTemplateMetaAlerts } from '@/components/settings/AutomationTemplateMetaAlerts'
import {
  buildWebPortalVariableMapping,
  normalizeWebPortalMappingForApi,
  normalizeWebPortalMappingFromApi,
} from '@/components/settings/webPortalTemplateMapping'
import { TemplateVariablesHelpDialog } from '@/components/actions/TemplateVariablesHelpDialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { META_INTEGRATION_ENABLED } from '@/config/env'
import { useAuth } from '@/hooks/use-auth'
import { log } from '@/lib/logger'
import { useWhatsAppStatusQuery } from '@/queries'
import { whatsappService, type WABA, type WhatsAppTemplate } from '@/services/whatsappService'
import {
  webPortalTemplatesService,
  type WebPortalAvailableField,
  type WebPortalSendType,
  type WebPortalTemplateConfig,
  type WebPortalVariableMappingEntry,
} from '@/services/webPortalTemplatesService'

type SendTypeMeta = {
  send_type: OwnSendType
  title: string
  description: string
  icon: typeof Bell
  expected: number
}

const SEND_TYPES: SendTypeMeta[] = [
  {
    send_type: 'search_alert_match',
    title: 'Cruces de búsqueda (resumen)',
    description: 'Aviso cuando hay nuevas propiedades que cruzan con una búsqueda guardada del usuario.',
    icon: Bell,
    expected: 2,
  },
  {
    send_type: 'price_drop',
    title: 'Bajada de precio',
    description: 'Aviso cuando baja el precio de una propiedad que el usuario sigue o que cruza con su búsqueda.',
    icon: TrendingDown,
    expected: 3,
  },
]

const HELP_VARIABLES = [
  { slot: 'Cruces · {{1}}', label: 'Nº de propiedades nuevas', source: 'Portal → recuento del resumen' },
  { slot: 'Cruces · {{2}}', label: 'Nombre de la búsqueda guardada', source: 'Portal → búsqueda del usuario' },
  { slot: 'Bajada · {{1}}', label: 'Título de la propiedad', source: 'Portal → propiedad' },
  { slot: 'Bajada · {{2}}', label: 'Precio anterior', source: 'Portal → precio previo' },
  { slot: 'Bajada · {{3}}', label: 'Precio nuevo', source: 'Portal → precio actual' },
  { slot: 'Botón 1', label: 'Ver propiedad', source: 'URL a la ficha de la propiedad' },
  { slot: 'Botón 2', label: 'Gestionar avisos', source: 'Magic link de un solo uso al perfil' },
]

function countTemplateBodyVariables(template: WhatsAppTemplate): number {
  const body = template.components.find((component) => component.type?.toUpperCase() === 'BODY')
  if (!body?.text) return 0
  const matches = body.text.match(/\{\{\d+\}\}/g)
  return matches ? matches.length : 0
}

// Los avisos que gestiona ESTA tarjeta, declarados en positivo. La union
// compartida WebPortalSendType tiene ocho miembros y los otros seis tienen su
// propia tarjeta; sin acotarlo, el Record de abajo exige rellenar las ocho.
// Extract mantiene la comprobacion de que estas dos existen en el tipo
// compartido.
type OwnSendType = Extract<WebPortalSendType, 'search_alert_match' | 'price_drop'>

type RowState = {
  template_name: string
  template_language: string
  waba_id: string
  enabled: boolean
  variable_mapping: WebPortalVariableMappingEntry[]
  available_fields: WebPortalAvailableField[]
  toggling: boolean
}

const emptyRow = (): RowState => ({
  template_name: '',
  template_language: 'es_ES',
  waba_id: '',
  enabled: false,
  variable_mapping: [],
  available_fields: [],
  toggling: false,
})

type DialogDraft = {
  waba_id: string
  template_key: string
  variable_mapping: WebPortalVariableMappingEntry[]
}

const EMPTY_DRAFT: DialogDraft = { waba_id: '', template_key: '', variable_mapping: [] }

export function WebPortalNotificationsCard() {
  const { lang = 'es' } = useParams()
  const { token } = useAuth()
  const hasAuthToken = Boolean(token && token.trim())
  const [isOpen, setIsOpen] = useState(false)

  const { data: whatsappStatus, isLoading: whatsappStatusLoading } = useWhatsAppStatusQuery({
    enabled: META_INTEGRATION_ENABLED && hasAuthToken,
  })
  const whatsappConnected = Boolean(whatsappStatus?.connected)
  const canConfigureTemplates = META_INTEGRATION_ENABLED && whatsappConnected

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const [rows, setRows] = useState<Record<OwnSendType, RowState>>({
    search_alert_match: emptyRow(),
    price_drop: emptyRow(),
  })

  const [activeSendType, setActiveSendType] = useState<OwnSendType | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState<DialogDraft>(EMPTY_DRAFT)
  const [wabas, setWabas] = useState<WABA[]>([])
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])

  const activeMeta = useMemo(
    () => SEND_TYPES.find((item) => item.send_type === activeSendType) ?? null,
    [activeSendType],
  )
  const activeRow = activeSendType ? rows[activeSendType] : null

  const applyConfig = (data: WebPortalTemplateConfig[]) => {
    setRows((prev) => {
      const next = { ...prev }
      for (const cfg of data) {
        // La API devuelve los ocho tipos de la union compartida; esta tarjeta
        // solo gestiona los suyos. El `in` ya lo comprobaba en ejecucion, pero
        // TypeScript no estrecha con eso: el predicado hace que la comprobacion
        // de ejecucion y la de tipos sean la misma, en vez de dos.
        const esDeEstaTarjeta = (s: WebPortalSendType): s is OwnSendType => s in next
        if (esDeEstaTarjeta(cfg.send_type)) {
          const meta = SEND_TYPES.find((item) => item.send_type === cfg.send_type)
          next[cfg.send_type] = {
            ...next[cfg.send_type],
            template_name: cfg.template_name,
            template_language: cfg.template_language || 'es_ES',
            waba_id: cfg.waba_id,
            enabled: cfg.enabled,
            variable_mapping: buildWebPortalVariableMapping(
              meta?.expected ?? 0,
              normalizeWebPortalMappingFromApi(cfg.variable_mapping),
            ),
            available_fields: cfg.available_fields || [],
          }
        }
      }
      return next
    })
  }

  const loadConfig = async () => {
    if (!hasAuthToken) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await webPortalTemplatesService.getConfig()
      applyConfig(response.data || [])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo cargar la configuración'
      log.error('Web portal notifications config load failed', { message })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAuthToken, token])

  const loadTemplates = async (wabaId: string) => {
    setTemplates([])
    try {
      const data = await whatsappService.getTemplates(wabaId)
      setTemplates(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las plantillas'
      log.error('Web portal notifications template load failed', { message, wabaId })
      setDialogError(message)
    }
  }

  const openDialog = async (sendType: OwnSendType) => {
    const meta = SEND_TYPES.find((item) => item.send_type === sendType)
    const row = rows[sendType]
    setActiveSendType(sendType)
    setDialogError(null)
    setDraft({
      waba_id: row.waba_id,
      template_key: row.template_name ? `${row.template_name}:${row.template_language}` : '',
      variable_mapping: row.variable_mapping,
    })
    setDialogOpen(true)
    setDialogLoading(true)
    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      const preferredWabaId = (row.waba_id && availableWabas.some((item) => item.id === row.waba_id)
        ? row.waba_id
        : availableWabas[0]?.id) || ''
      setDraft((prev) => ({ ...prev, waba_id: preferredWabaId }))
      if (!availableWabas.length) {
        setDialogError('No hay ninguna cuenta de WhatsApp conectada.')
      }
      if (preferredWabaId) {
        const loadedTemplates = await whatsappService.getTemplates(preferredWabaId)
        setTemplates(loadedTemplates)
        if (row.template_name) {
          const selected = loadedTemplates.find(
            (item) => `${item.name}:${item.language}` === `${row.template_name}:${row.template_language}`,
          )
          if (selected) {
            setDraft((prev) => ({
              ...prev,
              variable_mapping: buildWebPortalVariableMapping(meta?.expected ?? 0, row.variable_mapping),
            }))
          }
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudieron cargar las cuentas de WhatsApp'
      log.error('Web portal notifications WABA load failed', { message })
      setDialogError(message)
    } finally {
      setDialogLoading(false)
    }
  }

  const handleWabaChange = async (value: string) => {
    setDraft((prev) => ({ ...prev, waba_id: value, template_key: '', variable_mapping: [] }))
    setDialogError(null)
    await loadTemplates(value)
  }

  const handleTemplateChange = (templateKey: string) => {
    setDraft((prev) => ({
      ...prev,
      template_key: templateKey,
      variable_mapping: buildWebPortalVariableMapping(activeMeta?.expected ?? 0, prev.variable_mapping),
    }))
  }

  const handleMappingChange = (position: number, sourceKey: string) => {
    setDraft((prev) => ({
      ...prev,
      variable_mapping: prev.variable_mapping.map((item) =>
        item.position === position ? { ...item, source_type: 'variable', source_key: sourceKey } : item,
      ),
    }))
  }

  const handleSaveDialog = async () => {
    if (!activeSendType || !activeMeta) return
    const selectedWaba = wabas.find((item) => item.id === draft.waba_id)
    const selectedTemplate = templates.find((item) => `${item.name}:${item.language}` === draft.template_key)
    if (!selectedWaba || !selectedTemplate) {
      setDialogError('Selecciona una cuenta de WhatsApp y una plantilla.')
      return
    }
    const count = countTemplateBodyVariables(selectedTemplate)
    if (count !== activeMeta.expected) {
      setDialogError(`La plantilla tiene ${count} variables; este aviso requiere exactamente ${activeMeta.expected}.`)
      return
    }
    if (activeMeta.expected > 0 && draft.variable_mapping.some((item) => !item.source_key)) {
      setDialogError('Mapea cada variable de la plantilla a un dato disponible.')
      return
    }

    setSaving(true)
    setDialogError(null)
    try {
      const response = await webPortalTemplatesService.putConfig({
        send_type: activeSendType,
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language,
        waba_id: selectedWaba.id,
        enabled: rows[activeSendType].enabled,
        variable_mapping: normalizeWebPortalMappingForApi(draft.variable_mapping),
      })
      const saved = response.data
      setRows((prev) => ({
        ...prev,
        [activeSendType]: {
          ...prev[activeSendType],
          template_name: saved.template_name,
          template_language: saved.template_language || 'es_ES',
          waba_id: saved.waba_id,
          enabled: saved.enabled,
          variable_mapping: buildWebPortalVariableMapping(
            activeMeta.expected,
            normalizeWebPortalMappingFromApi(saved.variable_mapping),
          ),
          available_fields: saved.available_fields || prev[activeSendType].available_fields,
        },
      }))
      setDialogOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar'
      log.error('Web portal notifications save failed', { message, sendType: activeSendType })
      setDialogError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (sendType: OwnSendType, enabled: boolean) => {
    const row = rows[sendType]
    if (!row.template_name) return
    setRows((prev) => ({ ...prev, [sendType]: { ...prev[sendType], toggling: true } }))
    setError(null)
    try {
      const response = await webPortalTemplatesService.putConfig({
        send_type: sendType,
        template_name: row.template_name,
        template_language: row.template_language,
        waba_id: row.waba_id,
        enabled,
        variable_mapping: normalizeWebPortalMappingForApi(row.variable_mapping),
      })
      const saved = response.data
      setRows((prev) => ({
        ...prev,
        [sendType]: { ...prev[sendType], enabled: saved.enabled, toggling: false },
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar el estado'
      log.error('Web portal notifications toggle failed', { message, sendType })
      setError(message)
      setRows((prev) => ({ ...prev, [sendType]: { ...prev[sendType], toggling: false } }))
    }
  }

  const anyEnabled = useMemo(
    () => SEND_TYPES.some((meta) => rows[meta.send_type].enabled),
    [rows],
  )

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando notificaciones del portal web...
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <SeccionPlegable
        numero="07"
        etiqueta="Portal web"
        titulo="Notificaciones del portal web"
        descripcion="Plantilla y estado para los avisos de cruces de búsqueda y bajadas de precio que envía el portal."
        estado={
          <>
{!canConfigureTemplates ? (
                    <Badge variant="outline" className="ml-1 border-warning/40 text-warning">
                      Vista local
                    </Badge>
                  ) : anyEnabled ? (
                    <Badge variant="outline" className="ml-1 border-success/40 text-success">
                      Activa
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="ml-1">
                      Pausada
                    </Badge>
                  )}
          </>
        }
        abierta={isOpen}
        onAlternar={() => setIsOpen((prev) => !prev)}
        acciones={
          <>
<Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="shrink-0 rounded-full text-muted-foreground"
              aria-label="Ayuda variables notificaciones del portal web"
              onClick={() => setHelpOpen(true)}
            >
              <Info className="h-4 w-4" />
            </Button>
          </>
        }
      >
              <AutomationTemplateMetaAlerts
                lang={lang}
                metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                whatsappConnected={whatsappConnected}
                whatsappStatusLoading={whatsappStatusLoading}
              />

              {error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Notificación</TableHead>
                      <TableHead>Plantilla</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {SEND_TYPES.map((meta) => {
                      const row = rows[meta.send_type]
                      const RowIcon = meta.icon
                      return (
                        <TableRow key={meta.send_type}>
                          <TableCell>
                            <div className="flex items-center gap-2 font-medium text-foreground">
                              <RowIcon className="h-4 w-4 text-primary" />
                              {meta.title}
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground">{meta.description}</p>
                          </TableCell>
                          <TableCell>
                            {row.template_name ? (
                              <>
                                <div className="font-medium">{row.template_name}</div>
                                <div className="text-xs text-muted-foreground">{row.template_language}</div>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Sin configurar</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={row.enabled}
                                disabled={!canConfigureTemplates || !row.template_name || row.toggling}
                                onCheckedChange={(checked) => void handleToggle(meta.send_type, checked)}
                              />
                              <span className="text-xs text-muted-foreground">
                                {row.enabled ? 'Activo' : 'Pausado'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!canConfigureTemplates}
                              onClick={() => void openDialog(meta.send_type)}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
      </SeccionPlegable>

      <TemplateVariablesHelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        title="Variables de las notificaciones del portal web"
        description="El portal construye las variables con un orden fijo; aquí solo eliges qué plantilla aprobada las recibe y si el aviso está activo."
        orderNote="El teléfono del usuario no es una variable de plantilla: se usa como destinatario del WhatsApp."
        variablesTitle="Variables por aviso"
        sourceLabel="Origen:"
        variables={HELP_VARIABLES}
        examples={[
          {
            title: 'Cruces de búsqueda',
            text: 'Tienes {{1}} nuevas propiedades para tu búsqueda «{{2}}».',
          },
          {
            title: 'Bajada de precio',
            text: '«{{1}}» ha bajado de {{2}} a {{3}}.',
          },
        ]}
        note="El portal envía los valores ya resueltos; el dashboard solo decide la plantilla y el estado."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeMeta?.title ?? 'Configurar plantilla'}</DialogTitle>
          </DialogHeader>

          {dialogLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando plantillas de Meta...
            </div>
          ) : (
            <div className="space-y-4">
              {dialogError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{dialogError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label>Cuenta de WhatsApp</Label>
                <Select value={draft.waba_id || undefined} onValueChange={(value) => void handleWabaChange(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar WABA" />
                  </SelectTrigger>
                  <SelectContent>
                    {wabas.map((waba) => (
                      <SelectItem key={waba.id} value={waba.id}>
                        {waba.name || waba.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plantilla aprobada de Meta</Label>
                <Select
                  value={draft.template_key || undefined}
                  onValueChange={handleTemplateChange}
                  disabled={!draft.waba_id || templates.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem
                        key={`${template.name}:${template.language}`}
                        value={`${template.name}:${template.language}`}
                      >
                        {template.name} ({template.language}) — {countTemplateBodyVariables(template)} vars
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {draft.variable_mapping.length > 0 ? (
                <div className="space-y-2">
                  <Label>Mapa de variables</Label>
                  <p className="text-xs text-muted-foreground">
                    Asigna cada hueco de la plantilla a un dato que envía el portal web.
                  </p>
                  {draft.variable_mapping.map((mapping) => (
                    <div key={mapping.position} className="grid grid-cols-[64px_1fr] items-center gap-2">
                      <span className="text-sm font-medium">{`{{${mapping.position}}}`}</span>
                      <Select
                        value={mapping.source_key || undefined}
                        onValueChange={(value) => handleMappingChange(mapping.position, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar dato del portal" />
                        </SelectTrigger>
                        <SelectContent>
                          {(activeRow?.available_fields ?? []).map((field) => (
                            <SelectItem key={field.key} value={field.key}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">
                    <span className="font-mono text-foreground">Botón 1</span> Ver propiedad ·{' '}
                    <span className="font-mono text-foreground">Botón 2</span> Gestionar avisos (magic link)
                  </p>
                </div>
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={saving || dialogLoading || !draft.template_key}
              onClick={() => void handleSaveDialog()}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
